import { Canvas, Text } from 'fabric'
import { createTextOnCanvas } from '../utils/textObjectUtils'
import { getTextObjects } from '../../utils/text/textObject'
import { removeTextObject } from '../utils/textObjectUtils'
import { calculateTextPosition } from '../../utils/text/textPosition'
import { clampFontSize } from '../../utils/validation'
import { convertHexWithAlphaToRgba } from '../../utils/color/colorConversion'
import {
    applyTextBorderStyles,
    setupTextObjectEventHandlers,
} from '../../utils/text/textObject'
import {
    DEFAULT_TEXT_POSITION,
    TEXT_OBJECT_PADDING,
    TEXT_OBJECT_BACKGROUND_BORDER_RADIUS,
} from '../../constants'
import { applyOptimalRenderingSettings } from '../utils/canvasUtils'

/**
 * Применение подложки к текстовому объекту
 */
function applyTextObjectBackground(
    textObj: Text,
    hasBackground: boolean,
    backgroundColor: string | null
): void {
    if (hasBackground && backgroundColor !== null) {
        const rgbaBackgroundColor = convertHexWithAlphaToRgba(backgroundColor)
        textObj.set({
            textBackgroundColor: rgbaBackgroundColor,
            textBackgroundPadding: TEXT_OBJECT_PADDING.BACKGROUND,
            textBackgroundBorderRadius: TEXT_OBJECT_BACKGROUND_BORDER_RADIUS,
        })
    } else {
        textObj.set({
            textBackgroundColor: undefined,
            textBackgroundPadding: undefined,
            textBackgroundBorderRadius: undefined,
        })
    }
}
import type {
    MarkingPosition,
    TextAlignment,
    FontFamily,
    FabricObjectModifiedEvent,
} from '../../types'

/**
 * Параметры для создания текстового объекта
 */
export interface CreateTextObjectParams {
    text: string
    fontSize: number
    textColor: string
    textAlignment: TextAlignment
    fontFamily: FontFamily
    hasBackground: boolean
    backgroundColor: string | null
    position: MarkingPosition | null
    positionRef: React.MutableRefObject<MarkingPosition | null>
    fabricCanvas: Canvas
    safeArea: {
        top: number
        bottom: number
        left: number
        right: number
    }
    onFontSizeChange?: (size: number) => void
    onPositionChange?: (position: MarkingPosition) => void
    handleTextScaling: () => void
    handleTextModified: (e: FabricObjectModifiedEvent) => void
    isScalingFromSlider: () => boolean
}

/**
 * Создание нового текстового объекта на canvas
 * @returns созданный текстовый объект или null, если создание не удалось
 */
export async function createTextObject(
    params: CreateTextObjectParams
): Promise<Text | null> {
    const {
        text,
        fontSize,
        textColor,
        textAlignment,
        fontFamily,
        hasBackground,
        backgroundColor,
        position,
        positionRef,
        fabricCanvas,
        safeArea,
        onFontSizeChange,
        onPositionChange,
        handleTextScaling,
        handleTextModified,
        isScalingFromSlider,
    } = params

    // Удаляем все текстовые объекты с canvas перед созданием нового
    const remainingTextObjects = getTextObjects(fabricCanvas)
    remainingTextObjects.forEach((textObj) => {
        removeTextObject(fabricCanvas, textObj)
    })

    // Используем позицию из ref
    const currentPosition = positionRef.current
    const canvasWidth = fabricCanvas.width
    const canvasHeight = fabricCanvas.height

    // Рассчитываем максимальную ширину текста с учетом padding
    const maxWidth = calculateTextMaxWidth(
        canvasWidth,
        hasBackground,
        backgroundColor
    )

    // Создаем временный текстовый объект для получения размеров
    const rgbaColor = convertHexWithAlphaToRgba(textColor)
    const clampedFontSize = clampFontSize(fontSize)
    const defaultPosition = {
        x: DEFAULT_TEXT_POSITION.X,
        y: DEFAULT_TEXT_POSITION.Y,
    }
    const measurementTextObject = await createTextOnCanvas({
        text,
        fontSize: clampedFontSize,
        textColor: rgbaColor,
        textAlignment,
        fontFamily,
        position: currentPosition ?? defaultPosition,
        hasBackground,
        backgroundColor,
        canvasWidth,
        canvasHeight,
        safeArea,
        fabricCanvas,
        isScalingFromSlider,
    })

    // Получаем размеры текста
    const textWidth =
        measurementTextObject.width * (measurementTextObject.scaleX ?? 1)
    const textHeight =
        measurementTextObject.height * (measurementTextObject.scaleY ?? 1)

    // Рассчитываем позицию текста
    const positionToUse: MarkingPosition = currentPosition ?? defaultPosition

    const textPosition = calculateTextPosition({
        position: positionToUse,
        canvasWidth,
        canvasHeight,
        textAlign: textAlignment,
        textWidth,
        textHeight,
        safeArea,
    })

    // Создаем новый текстовый объект
    const textObj = await createTextOnCanvas({
        text,
        fontSize: clampedFontSize,
        textColor: rgbaColor,
        textAlignment,
        fontFamily,
        position: positionToUse,
        hasBackground,
        backgroundColor,
        canvasWidth,
        canvasHeight,
        safeArea,
        fabricCanvas,
        onFontSizeChange,
        onPositionChange,
        isScalingFromSlider,
    })

    // Проверяем еще раз, не был ли создан объект другим эффектом
    const currentTextObjects = getTextObjects(fabricCanvas)

    // Если уже есть текстовый объект на canvas (созданный другим эффектом), удаляем наш
    if (
        currentTextObjects.length > 0 &&
        !currentTextObjects.includes(textObj)
    ) {
        removeTextObject(fabricCanvas, textObj)
        return null
    }

    // Устанавливаем позицию и принудительно применяем цвет
    // Округляем координаты до целых пикселей для четкости текста
    const finalRgbaColor = convertHexWithAlphaToRgba(textColor)
    textObj.set({
        left: Math.round(textPosition.left),
        top: Math.round(textPosition.top),
        fill: finalRgbaColor,
    })

    // Настраиваем обработчики событий
    setupTextObjectEventHandlers({
        textObj,
        fabricCanvas,
        onScaling: handleTextScaling,
        onPositionChange: onPositionChange ? handleTextModified : undefined,
    })

    // Добавляем текстовый объект на canvas
    fabricCanvas.add(textObj)
    fabricCanvas.setActiveObject(textObj)

    // Применяем настройки обводки
    applyTextBorderStyles(textObj)

    // Обновляем настройки рендеринга canvas для оптимальной четкости текста
    // Для маленьких шрифтов используем более резкие настройки
    const canvasElement = fabricCanvas.getElement()
    if (canvasElement) {
        const ctx = canvasElement.getContext('2d')
        if (ctx) {
            applyOptimalRenderingSettings(ctx, clampedFontSize)
        }
    }

    // Принудительно применяем цвет еще раз после всех операций
    textObj.set({
        fill: finalRgbaColor,
    })

    // Для Inter/Montserrat дополнительно пересчитываем размеры через requestAnimationFrame
    // Это гарантирует, что размеры будут пересчитаны после применения шрифта
    if (fontFamily === 'Inter' || fontFamily === 'Montserrat') {
        requestAnimationFrame(() => {
            if (textObj && fabricCanvas.getObjects().includes(textObj)) {
                // Принудительно применяем charSpacing для проблемных шрифтов
                // Это важно, так как charSpacing может быть сброшен при обновлении объекта
                textObj.set({ charSpacing: -15 })
                
                textObj.initDimensions()

                // Применяем подложку после initDimensions()
                applyTextObjectBackground(
                    textObj,
                    hasBackground,
                    backgroundColor
                )

                // Принудительно применяем цвет с прозрачностью
                textObj.set({ fill: finalRgbaColor })

                textObj.setCoords()
                fabricCanvas.renderAll()
            }
        })
    }

    // Обновляем координаты и перерисовываем
    textObj.setCoords()
    fabricCanvas.renderAll()

    return textObj
}

/**
 * Расчет максимальной ширины текста с учетом padding
 */
function calculateTextMaxWidth(
    canvasWidth: number,
    hasBackground: boolean,
    backgroundColor: string | null
): number {
    const borderPadding = TEXT_OBJECT_PADDING.BORDER * 2
    const backgroundPadding =
        hasBackground && backgroundColor !== null
            ? TEXT_OBJECT_PADDING.BACKGROUND * 2
            : 0
    return Math.max(
        TEXT_OBJECT_PADDING.MIN_WIDTH,
        canvasWidth - borderPadding - backgroundPadding
    )
}
